import React from 'react';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { Status } from '../../../../types/redux/data/dataTypes';
import CustomTable from '../../../tables/CustomTable';
import {
  addCommasToNumbersAndRound,
  toTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import { mapStatusToColor } from '../../../../utilities/colorUtilities';
import { CheckCircle } from '@mui/icons-material';
import FundNameWithDemoPill from '../../../ui/FundNameWithDemoPill';
import { useDemoFundIds } from '../../../../hooks/useDemoFundIds';
import { demoRowStyle } from '../../../../utilities/demoFundUtils';
import type {
  GlobalExposurePayload,
  GlobalExposureRow,
} from '../../../../api/rs-hg/risk/globalExposure.types';

interface GlobalExposureTableProps {
  rows: GlobalExposurePayload;
  section: string;
  pageId?: string;
}

interface GlobalExposureData {
  fundId: string;
  fund: string;
  isDemoFund: boolean;
  navDate?: string;
  nav?: number;
  currency?: string;
  globalExpMethod?: string;
  fundVar?: number;
  // if it's commitment its blank, if var
  varApproach: boolean;
  commitment?: number;
  maxHundred: boolean;
  sumOfNotionals?: number;
  sept8?: number;
  oct8?: number;
  apr9?: number;
  july11?: number;
  aug11?: number;
}

export function generateGlobalExposureColumns(): CustomColumn<GlobalExposureData>[] {
  return [
    {
      title: 'Fund',
      field: 'fund',
      cellStyle: {
        textAlign: 'left',
      },
      headerStyle: {
        textAlign: 'left',
      },
      render: (rowData: GlobalExposureData) => (
        <FundNameWithDemoPill
          isDemo={rowData.isDemoFund}
          className="flex-wrap gap-y-1"
        >
          {rowData.fund}
        </FundNameWithDemoPill>
      ),
    },
    {
      title: 'NAV Date',
      field: 'navDate',
      headerStyle: {
        backgroundColor: '#65ff66',
      },
    },
    {
      title: 'NAV',
      field: 'nav',
      render: (rowData: any) => addCommasToNumbersAndRound(rowData.nav),
      headerStyle: {
        backgroundColor: '#65ff66',
      },
      cellStyle: {
        textAlign: 'right',
      },
    },
    {
      title: 'Currency',
      field: 'currency',
      headerStyle: {
        backgroundColor: '#65ff66',
      },
    },
    {
      title: 'Global Exp Method',
      field: 'globalExpMethod',
      headerStyle: {
        backgroundColor: 'rgb(255,153,0)',
      },
      cellStyle: (rowData: any) => {
        if (rowData !== 'Commitment') {
          return {
            fontWeight: 'bold',
          };
        } else {
          return {
            fontWeight: 'normal',
          };
        }
      },
    },
    {
      title: 'Fund VaR (% NAV)',
      field: 'fundVar',
      render: (rowData: any) => toTwoDecimalPlaces(rowData.fundVar),
      headerStyle: {
        backgroundColor: 'rgb(255,153,0)',
        textAlign: 'center',
      },
      cellStyle: (rowData: any, totalRowData: any) => {
        if (totalRowData.globalExpMethod !== 'Commitment') {
          return {
            fontWeight: 'bold',
            backgroundColor: '#FF9966',
          };
        } else {
          return {
            fontWeight: 'normal',
            backgroundColor: '#FF9966',
          };
        }
      },
    },
    {
      title: 'VaR Approach',
      field: 'varApproach',
      headerStyle: {
        backgroundColor: 'rgb(255,153,0)',
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
      render: (rowData: any) =>
        rowData.varApproach ? (
          <CheckCircle style={{ color: mapStatusToColor(Status.Pass) }} />
        ) : (
          ''
        ),
    },
    {
      title: 'Commitment (% NAV)',
      field: 'commitment',
      render: (rowData: any) => toTwoDecimalPlaces(rowData.commitment),
      headerStyle: {
        backgroundColor: 'rgb(255,153,0)',
      },
      cellStyle: {
        backgrondColor: '#FFBB99',
      },
    },
    {
      title: 'Max 100%',
      field: 'maxHundred',
      headerStyle: {
        backgroundColor: 'rgb(255,153,0)',
      },
      render: (rowData: any) =>
        rowData.maxHundred ? (
          <CheckCircle style={{ color: mapStatusToColor(Status.Pass) }} />
        ) : (
          ''
        ),
    },
    {
      title: 'Sum of Notionals (% NAV)',
      field: 'sumOfNotionals',
      headerStyle: {
        backgroundColor: 'rgb(255,153,0)',
      },
    },
    {
      title: 'Sept 2008',
      field: 'sept8',
      headerStyle: {
        backgroundColor: '#B3B3FF',
      },
    },
    {
      title: 'Oct 2008',
      field: 'oct8',
      headerStyle: {
        backgroundColor: '#B3B3FF',
      },
    },
    {
      title: 'Apr 2009',
      field: 'apr9',
      headerStyle: {
        backgroundColor: '#B3B3FF',
      },
    },
    {
      title: 'July 2011',
      field: 'july11',
      headerStyle: {
        backgroundColor: '#B3B3FF',
      },
    },
    {
      title: 'Aug 2011',
      field: 'aug11',
      headerStyle: {
        backgroundColor: '#B3B3FF',
      },
    },
  ].map((col: any) => {
    return {
      ...col,
      cellStyle:
        typeof col.cellStyle === 'function'
          ? (rowData, totalRowData) => ({
              textAlign: 'center',
              ...col.cellStyle(rowData, totalRowData),
            })
          : {
              textAlign: 'center',
              ...col.cellStyle,
            },
      headerStyle: {
        textAlign: 'center',
        ...col.headerStyle,
      },
    };
  });
}

function handleVarGlobalExp(globalExpRule: string, fundVarVal: number) {
  if (globalExpRule === 'VaR 8.0% max') {
    return fundVarVal < 8;
  } else if (globalExpRule === 'VaR 15.0% max') {
    return fundVarVal < 15;
  } else if (globalExpRule === 'VaR 18.0% max') {
    return fundVarVal < 18;
  } else if (
    globalExpRule === 'VaR 20.0% max' ||
    globalExpRule === 'VaR 20% max'
  ) {
    return fundVarVal < 20;
  } else {
    throw new Error('Unspecified Global Exp Value: ' + globalExpRule);
  }
}

function generateGlobalExposureData(
  rows: GlobalExposurePayload,
  demoFundIds: Set<string> = new Set(),
): GlobalExposureData[] {
  if (!rows?.length) return [];

  return rows.map((datum: GlobalExposureRow) => {
    const fundId = datum[0];
    const aggregate = datum[2];
    const varApproachIsTrue =
      aggregate.var_fund === 'yes' &&
      aggregate.gl_exp_method != null &&
      aggregate.fund_var != null
        ? handleVarGlobalExp(aggregate.gl_exp_method, aggregate.fund_var)
        : false;

    return {
      fundId,
      fund: datum[1],
      isDemoFund: demoFundIds.has(fundId),
      navDate: aggregate.selected_position_date,
      nav: aggregate.nav,
      currency: aggregate.base_currency,
      globalExpMethod: aggregate.gl_exp_method,
      fundVar: aggregate.fund_var,
      varApproach: varApproachIsTrue,
      commitment: aggregate.commitment,
      maxHundred:
        aggregate.var_fund === 'no'
          ? aggregate.commitment != null && aggregate.commitment < 100
          : varApproachIsTrue,
      sumOfNotionals: aggregate.sum_of_notionals,
      sept8: aggregate.hst?.[0],
      oct8: aggregate.hst?.[1],
      apr9: aggregate.hst?.[2],
      july11: aggregate.hst?.[3],
      aug11: aggregate.hst?.[4],
    };
  });
}
/*
 * commitment fund and commitment < 100%
 * or var fund and commitment tick is green
 */
const GlobalExposureTable: React.FC<GlobalExposureTableProps> = (props) => {
  const demoFundIds = useDemoFundIds(props.section, props.pageId);
  const data = generateGlobalExposureData(props.rows, demoFundIds).sort(
    (a: GlobalExposureData, b: GlobalExposureData) => {
      if (a.fund.toUpperCase() < b.fund.toUpperCase()) return -1;
      else return 1;
    },
  );
  return (
    <CustomTable<GlobalExposureData>
      options={{
        paging: false,
        search: true,
        exportButton: true,
        rowStyle: demoRowStyle<GlobalExposureData>(),
      }}
      title="Overview"
      csvFields={generateGlobalExposureColumns().map((col: any) => col.field)}
      data={data}
      columns={generateGlobalExposureColumns()}
      showToolbar
    />
  );
};

export default GlobalExposureTable;
